<template>
    <v-container fluid>
        <!-- KPI: calcolati client-side sulla lista filtrata (nessun endpoint /api/pagamento/kpi
             nel contratto), stesso approccio "??  '—'" delle altre pagine. -->
        <v-row class="mb-2">
            <v-col cols="6" md="3">
                <v-card variant="outlined" class="rounded-lg" height="100%">
                    <v-card-text>
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-caption text-medium-emphasis">Da pagare</div>
                                <div class="text-h5 font-weight-bold">{{ formattaImporto(kpi.totaleDaPagare) }}</div>
                                <div class="text-caption text-medium-emphasis">{{ kpi.numeroDaPagare }} pagamenti</div>
                            </div>
                            <v-avatar color="blue-lighten-4" size="44">
                                <v-icon color="primary">mdi-cash-clock</v-icon>
                            </v-avatar>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="6" md="3">
                <v-card variant="outlined" class="rounded-lg" height="100%">
                    <v-card-text>
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-caption text-medium-emphasis">Scaduti</div>
                                <div class="text-h5 font-weight-bold">{{ formattaImporto(kpi.totaleScaduto) }}</div>
                                <div class="text-caption text-medium-emphasis">{{ kpi.numeroScaduto }} pagamenti</div>
                            </div>
                            <v-avatar color="red-lighten-4" size="44">
                                <v-icon color="error">mdi-alert-circle</v-icon>
                            </v-avatar>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="6" md="3">
                <v-card variant="outlined" class="rounded-lg" height="100%">
                    <v-card-text>
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="text-caption text-medium-emphasis">Pagati</div>
                                <div class="text-h5 font-weight-bold">{{ formattaImporto(kpi.totalePagato) }}</div>
                                <div class="text-caption text-medium-emphasis">{{ kpi.numeroPagato }} pagamenti</div>
                            </div>
                            <v-avatar color="green-lighten-4" size="44">
                                <v-icon color="success">mdi-check-circle</v-icon>
                            </v-avatar>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Filtri -->
        <v-row class="align-center mb-2">
            <v-col cols="12" sm="6" md="3">
                <v-select v-model="tipoPagamentoId" :items="tipiPagamento" item-title="nome" item-value="id"
                    label="Tutte le tipologie" density="compact" variant="outlined" hide-details clearable
                    @update:modelValue="carica" />
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-select v-model="fornitoreId" :items="fornitori" item-title="nome" item-value="id"
                    label="Tutti i fornitori" density="compact" variant="outlined" hide-details clearable
                    @update:modelValue="carica" />
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-select v-model="stato" :items="statiDisponibili" item-title="label" item-value="value"
                    label="Tutti gli stati" density="compact" variant="outlined" hide-details clearable
                    @update:modelValue="carica" />
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-text-field v-model="idDocumentoInterno" label="ID documento interno" density="compact"
                    variant="outlined" hide-details clearable />
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-text-field v-model="numDocumentoInterno" label="Numero documento interno" density="compact"
                    variant="outlined" hide-details clearable />
            </v-col>
            <v-col cols="12" class="d-flex flex-wrap justify-end" style="gap:8px">
                <v-btn variant="outlined" prepend-icon="mdi-cog-outline" to="/tipi-pagamento">Tipi pagamento</v-btn>
                <v-btn variant="outlined" prepend-icon="mdi-domain" to="/fornitori">Fornitori</v-btn>
                <v-btn color="primary" prepend-icon="mdi-plus" @click="apriDialog(null)">Nuovo pagamento</v-btn>
            </v-col>
        </v-row>

        <v-data-table :headers="headers" :items="pagamenti" :items-per-page="15" density="comfortable">
            <template #[`item.stato`]="{ item }">
                <v-chip size="small" :color="coloreRiga(item)" variant="flat">
                    {{ etichettaRiga(item) }}
                </v-chip>
            </template>

            <template #[`item.importo`]="{ item }">
                {{ formattaImporto(item.importo) }}
            </template>

            <template #[`item.dataScadenza`]="{ item }">
                {{ formatData(item.dataScadenza) }}
            </template>

            <template #[`item.dataPagamento`]="{ item }">
                {{ formatData(item.dataPagamento) }}
            </template>

            <template #[`item.beneficiario`]="{ item }">
                {{ item.fornitoreNome || item.beneficiarioNote || '—' }}
            </template>

            <template #[`item.azioni`]="{ item }">
                <v-btn v-if="item.stato === 'DaPagare'" size="small" variant="text" color="success"
                    @click="apriCompletamento(item)">
                    Segna pagato
                </v-btn>
                <v-btn icon variant="text" size="small" title="Duplica" @click="duplica(item)">
                    <v-icon>mdi-content-copy</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="small"
                    :title="item.stato === 'Pagato' ? 'Modifica note/data di pagamento' : 'Modifica'"
                    @click="modifica(item)">
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="small" color="error" @click="chiediEliminazione(item)">
                    <v-icon>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <!-- Un solo dialog CRUD: niente vista di dettaglio separata (vedi piano Fase 6). -->
        <dialog-pagamento v-model="dialog" :pagamento="pagamentoSelezionato" :valori-iniziali="valoriIniziali"
            :documenti-readonly="documentiReadonly" @salvato="onSalvato" />

        <dialog-completa-pagamento v-model="dialogCompletamento" :pagamento="pagamentoSelezionato"
            @completato="onCompletato" />

        <dialog-modifica-completamento v-if="pagamentoCompletamento" v-model="dialogModificaCompletamento"
            titolo="Modifica pagamento saldato" :sottotitolo="pagamentoCompletamento.tipoPagamentoNome"
            etichetta-data="Data pagamento"
            avviso-data="Se il prossimo pagamento è stato generato da questo (e non è ricorrente), verrà ricalcolato dalla nuova data."
            :data-esecuzione="pagamentoCompletamento.dataPagamento"
            :note="pagamentoCompletamento.notePagamento" :azione="salvaCompletamento" />

        <v-dialog v-model="dialogElimina" max-width="420">
            <v-card>
                <v-card-title>Confermi l'eliminazione?</v-card-title>
                <v-card-text>Il pagamento verrà rimosso.</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogElimina = false">Annulla</v-btn>
                    <v-btn color="error" @click="confermaEliminazione">Elimina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import DialogPagamento from "@/components/pagamenti/DialogPagamento.vue";
import DialogCompletaPagamento from "@/components/pagamenti/DialogCompletaPagamento.vue";
import DialogModificaCompletamento from "@/components/common/DialogModificaCompletamento.vue";
import { STATI_PAGAMENTO, coloreRiga, etichettaRiga, isScaduto } from "@/constants/statoPagamento.js";
import { leggiFiltriDaQuery, scriviFiltriSuQuery } from "@/utils/filtriUrl.js";
import storage from "@/services/storage.js";

export default {
    name: "PagamentiPage",

    components: {
        DialogPagamento,
        DialogCompletaPagamento,
        DialogModificaCompletamento,
    },

    data() {
        return {
            tipoPagamentoId: null,
            fornitoreId: null,
            stato: null,
            idDocumentoInterno: "",
            numDocumentoInterno: "",
            debounceDocumento: null,
            statiDisponibili: STATI_PAGAMENTO,
            dialog: false,
            pagamentoSelezionato: null,
            valoriIniziali: null,
            dialogCompletamento: false,
            dialogElimina: false,
            pagamentoDaEliminare: null,
            dialogModificaCompletamento: false,
            pagamentoCompletamento: null,
            headers: [
                { title: "Tipologia", key: "tipoPagamentoNome" },
                { title: "Beneficiario", key: "beneficiario" },
                { title: "Importo", key: "importo" },
                { title: "Scadenza", key: "dataScadenza" },
                { title: "Pagato il", key: "dataPagamento" },
                { title: "Stato", key: "stato" },
                { title: "ID doc. interno", key: "idDocumentoInterno" },
                { title: "N. doc. interno", key: "numDocumentoInterno" },
                { title: "", key: "azioni", sortable: false },
            ],
        };
    },

    computed: {
        // "readonly=1" in query: blocca la modifica di idDocumentoInterno/numDocumentoInterno
        // quando precompilati dall'URL in un nuovo pagamento (uso tipico: embed di terzi che
        // vuole imporre il legame col proprio documento). "readonly=0"/assente = modificabili.
        // Vincolato anche alla presenza effettiva di un valore da proteggere: se l'utente
        // rimuove i valori dai filtri (quindi spariscono dall'URL), non c'è più nulla da
        // bloccare e il campo torna scrivibile per un nuovo valore.
        documentiReadonly() {
            if (this.$route.query.readonly !== "1") return false;
            return !!(this.valoriDocumentoDaUrl());
        },
        pagamenti() {
            return this.$store.getters["pagamenti/lista"];
        },
        tipiPagamento() {
            return this.$store.getters["tipiPagamento/lista"];
        },
        fornitori() {
            return this.$store.getters["fornitori/lista"];
        },
        kpi() {
            const lista = this.pagamenti;
            const daPagare = lista.filter(p => p.stato === "DaPagare" && !isScaduto(p));
            const scaduti = lista.filter(p => isScaduto(p));
            const pagati = lista.filter(p => p.stato === "Pagato");
            const somma = arr => arr.reduce((tot, p) => tot + (p.importo || 0), 0);
            return {
                totaleDaPagare: somma(daPagare),
                numeroDaPagare: daPagare.length,
                totaleScaduto: somma(scaduti),
                numeroScaduto: scaduti.length,
                totalePagato: somma(pagati),
                numeroPagato: pagati.length,
            };
        },
    },

    watch: {
        // Ricerca testuale: debounce come in Macchine.vue, per non ricaricare/riscrivere
        // l'URL ad ogni tasto.
        idDocumentoInterno() {
            clearTimeout(this.debounceDocumento);
            this.debounceDocumento = setTimeout(() => this.carica(), 300);
        },
        numDocumentoInterno() {
            clearTimeout(this.debounceDocumento);
            this.debounceDocumento = setTimeout(() => this.carica(), 300);
        },
    },

    async mounted() {
        Object.assign(this, leggiFiltriDaQuery(this.$route, {
            tipoPagamentoId: "number",
            fornitoreId: "number",
            stato: "string",
            idDocumentoInterno: "string",
            numDocumentoInterno: "string",
        }));
        // Atteso PRIMA di carica(): altrimenti carica() ricostruirebbe la query da
        // this.$route.query non ancora aggiornato dalla replace() qui sotto (router.replace
        // è asincrono), riscrivendo nell'URL "nuovo" e i campi di precompilazione appena rimossi.
        await this.apriDaUrlSeRichiesto();
        this.ripristinaBozzaSePresente();
        this.$store.dispatch("tipiPagamento/carica");
        this.$store.dispatch("fornitori/carica");
        this.carica();
    },

    methods: {
        coloreRiga,
        etichettaRiga,

        // Link diretto tipo /pagamenti?nuovo=1&importo=150&descrizione=Bolletta:
        // apre subito il dialog di inserimento precompilato con i campi presenti
        // nell'URL, poi li rimuove dall'URL (un refresh successivo non deve
        // riaprire il dialog né rischiare di sovrascrivere modifiche già fatte a mano).
        async apriDaUrlSeRichiesto() {
            if (this.$route.query.nuovo !== "1") return;

            const CAMPI_PRECOMPILABILI = {
                tipoPagamentoId: "number",
                fornitoreId: "number",
                beneficiarioNote: "string",
                importo: "number",
                dataScadenza: "string",
                descrizione: "string",
                ricorrente: "boolean",
                idDocumentoInterno: "string",
                numDocumentoInterno: "string",
            };

            this.valoriIniziali = leggiFiltriDaQuery(this.$route, CAMPI_PRECOMPILABILI);
            this.pagamentoSelezionato = null;
            this.dialog = true;

            const query = { ...this.$route.query };
            delete query.nuovo;
            Object.keys(CAMPI_PRECOMPILABILI).forEach(chiave => delete query[chiave]);
            await this.$router.replace({ query }).catch(() => {});
        },

        // Riapre il dialog "Nuovo pagamento" con i valori lasciati dall'utente prima di
        // navigare verso Tipi pagamento/Fornitori (bottoni qui sopra): per quei link,
        // pensati per restare dentro la stessa operazione e non per una vera navigazione
        // nel sito (tipico di un embed in webview), l'utente si aspetta di ritrovare
        // tutto com'era. Se apriDaUrlSeRichiesto() ha già aperto il dialog da un link
        // "nuovo=1" fresco, quella precompilazione ha sempre la precedenza.
        ripristinaBozzaSePresente() {
            if (this.dialog) return;
            const bozza = storage.getBozza("pagamento");
            if (!bozza) return;

            this.pagamentoSelezionato = null;
            this.valoriIniziali = bozza;
            this.dialog = true;
        },

        async carica() {
            const filtri = this.filtriCorrenti();
            scriviFiltriSuQuery(this.$router, this.$route, filtri);
            try {
                await this.$store.dispatch("pagamenti/carica", filtri);
            } catch (error) {
                console.log(error);
            }
        },

        filtriCorrenti() {
            return {
                tipoPagamentoId: this.tipoPagamentoId || undefined,
                fornitoreId: this.fornitoreId || undefined,
                stato: this.stato || undefined,
                idDocumentoInterno: this.idDocumentoInterno || undefined,
                numDocumentoInterno: this.numDocumentoInterno || undefined,
            };
        },

        apriDialog(item) {
            this.pagamentoSelezionato = item;
            this.valoriIniziali = item ? null : this.valoriDocumentoDaUrl();
            this.dialog = true;
        },

        // Ogni nuovo pagamento aperto (non solo il primo via "nuovo=1") eredita
        // idDocumentoInterno/numDocumentoInterno correnti dall'URL, se presenti:
        // utile per un embed che vuole legare automaticamente ogni pagamento
        // creato al proprio documento, senza doverli reimpostare manualmente.
        valoriDocumentoDaUrl() {
            const valori = leggiFiltriDaQuery(this.$route, {
                idDocumentoInterno: "string",
                numDocumentoInterno: "string",
            });
            return (valori.idDocumentoInterno || valori.numDocumentoInterno) ? valori : null;
        },

        onSalvato() {
            this.carica();
        },

        apriCompletamento(item) {
            this.pagamentoSelezionato = item;
            this.dialogCompletamento = true;
        },

        onCompletato() {
            this.carica();
        },

        // Un pagamento già saldato non è più modificabile nei suoi dati: si possono solo
        // correggere note e data di pagamento (la lista non porta le note: si carica il dettaglio).
        async modifica(item) {
            if (item.stato !== "Pagato") {
                this.apriDialog(item);
                return;
            }
            try {
                await this.$store.dispatch("pagamenti/caricaDettaglio", item.id);
                this.pagamentoCompletamento = this.$store.getters["pagamenti/corrente"];
                this.$nextTick(() => {
                    this.dialogModificaCompletamento = true;
                });
            } catch (error) {
                console.log(error);
            }
        },

        async salvaCompletamento({ dataEsecuzione, note }) {
            await this.$store.dispatch("pagamenti/modificaCompletamento", {
                id: this.pagamentoCompletamento.id,
                dati: { dataEsecuzione, note },
                filtri: this.filtriCorrenti(),
            });
        },

        async duplica(item) {
            try {
                await this.$store.dispatch("pagamenti/duplica", { id: item.id, filtri: this.filtriCorrenti() });
            } catch (error) {
                console.log(error);
            }
        },

        chiediEliminazione(item) {
            this.pagamentoDaEliminare = item;
            this.dialogElimina = true;
        },

        async confermaEliminazione() {
            try {
                await this.$store.dispatch("pagamenti/elimina", {
                    id: this.pagamentoDaEliminare.id,
                    filtri: this.filtriCorrenti(),
                });
            } catch (error) {
                console.log(error);
            }
            this.dialogElimina = false;
        },

        formattaImporto(importo) {
            return (importo || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
        },

        formatData(data) {
            return data ? new Date(data).toLocaleDateString("it-IT") : "—";
        },
    },
};
</script>
